import { set } from '@ember/object';

import { clearAttribute } from 'beebee-base/lib/utility';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 *      keep in sync with the code):
 *   role:      lib — pure payload builders for the ecommerce order calls: they turn
 *              the cart plus the buyer contact into the JSON body
 *              /ecommerce_freeprice and /ecommerce_confirm expect.
 *   exports:   buildAddressObj(address), compattaProdotti(products),
 *              castSafeEvents(events), addPartecipanteObj(g, obj),
 *              pushPartecipanti(cart, list), buildCheckoutDataObj(input)
 *   callers:   controllers/checkout.js (createAddressObj, compattaProdotti,
 *              _castSafeEvents, addPartecipanteObj, pushPartecipanti, createDataObj),
 *              controllers/checkin.js (createAddressObj)
 *   tests:     checkout-order-test.js, checkout-getters-test.js,
 *              checkout-partecipanti-validation-test.js, checkin-test.js
 *   gotchas:   every `?? null` / `?? ''` here is load-bearing — JSON.stringify DROPS
 *              an undefined value and the pricing procedure concatenates these keys
 *              into a dynamic statement, so a missing key becomes SQL NULL and kills
 *              the whole call (POP-109). Read each comment before "simplifying" a
 *              default away.
 *
 * Split out of controllers/checkout.js: nothing here reads controller state, so the
 * shaping rules — and the reasoning behind every default — are testable and
 * greppable on their own.
 */

/**
 * Serializes a contact address into the `{ id, attributes }` shape the order
 * endpoints take, stripping the single quotes the procedure cannot escape.
 *
 * @param {object} firstAddress the address record to serialize
 * @returns {object|null} the payload entry, or null when there is no address
 */
export function buildAddressObj(firstAddress) {
  if (!firstAddress) {
    return null;
  }

  const clearAttribute = (value) => {
    if (typeof value === 'string') {
      return value.replace(/'/g, ' ');
    }
    return value;
  };

  const a = {
    id: firstAddress.id,
    attributes: {
      id: firstAddress.id,
      name: clearAttribute(firstAddress.name),
      icon: firstAddress.icon,
      view: firstAddress.view,
      place_id: firstAddress.place_id,
      location_position_lat: clearAttribute(firstAddress.location_position_lat),
      location_position_lng: clearAttribute(firstAddress.location_position_lng),
      location_address_text: clearAttribute(firstAddress.location_address_text),
      location_address_house: clearAttribute(firstAddress.location_address_house),
      location_address_street: clearAttribute(firstAddress.location_address_street),
      location_address_postal_code: clearAttribute(firstAddress.location_address_postal_code),
      location_address_city: clearAttribute(firstAddress.location_address_city),
      location_address_county: clearAttribute(firstAddress.location_address_county),
      location_address_state: clearAttribute(firstAddress.location_address_state),
      location_address_country: clearAttribute(firstAddress.location_address_country),
      location_address_country_code: clearAttribute(firstAddress.location_address_country_code),
      categories_id: firstAddress.categories_id,
      categories_title: clearAttribute(firstAddress.categories_title),
      type: firstAddress.type,
      is_invoice: firstAddress.is_invoice,
      accuracy: firstAddress.accuracy,
      heading: firstAddress.heading,
    },
  };

  for (const name in a) {
    a[name] = clearAttribute(a[name]);
  }

  return a;
}

/**
 * Merges the individual-lesson products (product_status 92) that share an id and a
 * name into a single entry carrying all their events, so the summary shows one line
 * per product instead of one per booked hour.
 *
 * @param {Array} products the summary products
 * @returns {Array} the merged list (empty for a non-array input)
 */
export function compattaProdotti(products) {
  var productComp = [];

  if (Array.isArray(products)) {
    products.forEach(function (product) {
      if (product.product_status_id === 92) {
        let tmpProduct = productComp.filter((item) => item.id === product.id);

        if (tmpProduct.length > 0 && tmpProduct[0]?.name === product.name) {
          tmpProduct = tmpProduct[0];

          tmpProduct.events.push(...product.events);
        } else {
          const tmpProduct = product;
          set(tmpProduct, 'files', null);
          productComp.push(tmpProduct);
        }
      } else {
        const tmpProduct = product;
        set(tmpProduct, 'files', null);
        productComp.push(tmpProduct);
      }
    });
  }

  return productComp;
}

/**
 * The procedure reads `n_pax`, `start_date` and `end_date` out of every event
 * and casts them; a missing key nulls the statement exactly like the
 * product-level keys do. `n_pax` is normally stamped by detail.js's pricing
 * task (which defaults it to 1), so mirror that default rather than inventing
 * a different one, and leave every other key of the hour untouched — the same
 * objects travel on to the order.
 */
export function castSafeEvents(events) {
  if (!Array.isArray(events)) {
    return [];
  }

  return events.map((e) => ({ ...e, n_pax: e?.n_pax ?? 1 }));
}

/**
 * Copies every participant of a cart entry into the payload entry's `partecipanti`.
 *
 * @param {object} g   the cart entry (group product or private lesson)
 * @param {object} obj the payload entry being built (mutated)
 */
export function addPartecipanteObj(g, obj) {
  for (let j = 0; j < g.partecipanti.length; j++) {
    const p = g.partecipanti[j];

    const par = {
      nome: p.contact?.first_name,
      cognome: p.contact?.last_name,
      data_nascita: p.contact?.data_nascita,
      cod_fiscale: p.contact?.cod_fiscale,
      id: p.contact?.id,
    };

    // Un solo colore in base al tipo di prodotto:
    // fondo -> fondo_color, snow -> snow_color, altrimenti color
    if (g.item?.is_fondo) {
      par.fondo_color = p.contact?.fondo_color;
    } else if (g.item?.is_snow) {
      par.snow_color = p.contact?.snow_color;
    } else {
      par.color = p.contact?.color;
    }

    obj.partecipanti.push(par);
  }
}

/**
 * Flattens a cart entry's participants (with their booked slot number) into a single
 * list, used by the summary views.
 *
 * @param {object} cart             a cart entry
 * @param {Array}  listPartecipanti the list being filled (mutated)
 */
export function pushPartecipanti(cart, listPartecipanti) {
  if (cart.partecipanti?.length > 0) {
    cart.partecipanti.forEach(function (e) {
      listPartecipanti.push({
        nome: e.contact?.first_name,
        cognome: e.contact?.last_name,
        cod_fiscale: e.contact?.cod_fiscale,
        mail: e.contact?.mail,
        id: e.contact?.id,
        color: e.contact?.color,
        snow_color: e.contact?.snow_color,
        fondo_color: e.contact?.fondo_color,
        number: e.number,
        data_nascita: e.contact?.data_nascita,
      });
    });
  }
}

/**
 * Builds the body posted to /ecommerce_freeprice and /ecommerce_confirm: the buyer
 * block (with its invoice address), then one entry per group product and per private
 * lesson in the cart.
 *
 * @param {object}  input
 * @param {object}  input.contact     the buyer contact record
 * @param {*}       input.gdpr        the tenant consent rows as answered
 * @param {object}  input.cart        applicationController.cart (voucher/groups/private)
 * @param {string}  [input.notes]     free-text note typed at checkout
 * @param {boolean} [input.isAlbergo] the buyer is a hotel booking for its guests
 * @param {string}  [input.promoCode] promo code typed at checkout
 * @returns {object} the request body
 */
export function buildCheckoutDataObj({
  contact,
  gdpr,
  cart: sourceCart,
  notes,
  isAlbergo,
  promoCode,
}) {
  const cart = {
    contact: {
      id: contact.get('id'),
      gdpr,
      fisica_giuridica: contact.get('fisica_giuridica'),
      ragione_sociale: clearAttribute(contact.get('ragione_sociale')),
      first_name: clearAttribute(contact.get('first_name')),
      last_name: clearAttribute(contact.get('last_name')),
      data_nascita: clearAttribute(contact.get('data_nascita')),
      luogo_nascita: clearAttribute(contact.get('luogo_nascita')),
      piva: clearAttribute(contact.get('piva')),
      cod_fiscale: clearAttribute(contact.get('cod_fiscale')),
      mail: clearAttribute(contact.get('mail')),
      mobile: clearAttribute(contact.get('mobile')),
      addresses: [],
    },
    groups: [],
    private: [],
    voucher: [...sourceCart.voucher],
    notes: notes || '',
    is_albergo: !!isAlbergo,
    promo_code: clearAttribute(promoCode) || '',
    custom_fields: [],
  };

  if (cart.promo_code) {
    cart.promo_code = (cart.promo_code || '').toUpperCase();
  }

  if (contact.get('addresses')[0]?.name) {
    cart.contact.addresses.push(buildAddressObj(contact.get('addresses')[0]));
  }

  for (var name in cart.contact) {
    cart.contact[name] = clearAttribute(cart.contact[name]);
  }

  for (let i = 0; i < sourceCart.groups.length; i++) {
    const g = sourceCart.groups[i];
    const obj = {
      // `?? null` is not cosmetic: JSON.stringify DROPS an undefined value, and
      // the pricing procedure concatenates these keys into a dynamic statement.
      // A missing key evaluates to SQL NULL, `text || NULL` is NULL, so the whole
      // statement becomes NULL and the call dies with "query string argument of
      // EXECUTE is null" — which /ecommerce_freeprice reports as a bare 400
      // "Parametro non valido" and dead-ends the checkout. Sending an explicit
      // null is accepted (verified against the live endpoint), sending nothing
      // is not — and the empty string rather than null because the procedure
      // echoes a JSON null back as the literal TEXT 'null', which the checkout
      // summary then shows to the customer ("… @ null").
      // See castSafeEvents above for the same trap inside `events`.
      // The real fix belongs to the procedure: Linear POP-109 (and POP-100).
      // `data_inizio`, `giorni`, `product_id` and `product_status_id` stay raw
      // on purpose — the procedure refuses them null as well as missing, so a
      // fallback would only invent a wrong price for an already broken entry.
      comprensorio: g.description ?? '',
      data_inizio: g.start_date,
      giorni: g.giorni,
      product_id: g.product_id,
      product_status_id: g.product_status_id,
      title: g.title ?? '',
      persone: g.partecipanti?.length || 0,
      modules: g.item.modules_json ?? null,
      block_check: g.item.block_check_json ?? null,
      // Cast target: the procedure runs `...::boolean` on this value before its
      // own `if(assicurazione is null)` default can fire, so a JSON null throws
      // "invalid input syntax for type boolean" instead of defaulting. Apply the
      // procedure's intended default here.
      assicurazione: g.assicurazione ?? false,
      partecipanti: [],
    };

    addPartecipanteObj(g, obj);

    cart.groups.push(obj);
  }

  for (let i = 0; i < sourceCart.private.length; i++) {
    const g = sourceCart.private[i];
    const maestroId = g.maestro?.id;
    const obj = {
      // Same trap as the groups branch above. `comprensorio` is the one that
      // actually blocked customers: it comes from `intComp || location`, both
      // undefined whenever the customer reaches the product without picking a
      // resort (no `?location=` in the URL), so the key vanished from the body
      // and every such checkout answered 400 "Parametro non valido" (POP-109).
      comprensorio: g.comprensorio ?? '',
      lingua: g.language ?? '',
      gender: g.gender ?? '',
      product_id: g.item.id,
      product_status_id: g.item.product_status_id,
      persone: g.partecipanti?.length || 0,
      modules: g.item.modules_json ?? null,
      block_check: g.item.block_check_json ?? null,
      assicurazione: g.assicurazione ?? false,
      events: castSafeEvents(g.ore),
      partecipanti: [],
    };

    // `maestro_id` is the opposite case: the procedure guards it with
    // `is not null` and then casts to ::bigint, but a JSON null reaches that
    // guard as the TEXT 'null', which passes it and blows up on the cast.
    // Omitting the key is the branch the procedure handles, so only send it
    // when there is a real instructor.
    if (maestroId !== null && maestroId !== undefined) {
      obj.maestro_id = maestroId;
    }

    addPartecipanteObj(g, obj);

    cart.private.push(obj);
  }

  return cart;
}
